import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { DEFAULT_ROLES, DEFAULT_CERT_TYPES } from '@cls/shared';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

const FALLBACK_DURATIONS = ['1 day', '3 days', '1 week', '2 weeks', '1 month', 'Until project end'] as const;

interface Project { id: string; code: string; name: string; status: string; }
interface Contact { id: string; name: string; role: string | null; customer: string | null; email: string | null; phone: string | null; }
interface RoleReq { role: string; mandatory: string[]; beneficial: string[]; other: string[] }

interface VacancyForEdit {
  id: string;
  projectId: string;
  role: string;
  startDate?: string | null;
  duration?: string | null;
  requiredSkills?: string[];
  statusFilter?: string[];
  requesterId?: string | null;
  notes?: string | null;
}

interface Props { onClose: () => void; editing?: VacancyForEdit | null }

export function AddVacancyModal({ onClose, editing }: Props) {
  const qc = useQueryClient();
  const { values: roles } = useReferenceList('roles', DEFAULT_ROLES);
  const { values: certTypes } = useReferenceList('cert-types', DEFAULT_CERT_TYPES);
  const { values: durations } = useReferenceList('durations', FALLBACK_DURATIONS);

  const [projectId, setProjectId] = useState(editing?.projectId ?? '');
  const [role, setRole] = useState<string>(editing?.role ?? '');
  const [requesterId, setRequesterId] = useState(editing?.requesterId ?? '');
  const [startDate, setStartDate] = useState(editing?.startDate?.slice(0, 10) ?? '');
  const [duration, setDuration] = useState(editing?.duration ?? '1 week');
  const [skills, setSkills] = useState<string[]>(editing?.requiredSkills ?? []);
  const userTouchedSkills = useRef(false);
  const initialStatuses = editing?.statusFilter ?? ['available', 'deployed'];
  const [stAvail, setStAvail] = useState(initialStatuses.includes('available'));
  const [stDep, setStDep] = useState(initialStatuses.includes('deployed'));
  const [stUnavail, setStUnavail] = useState(initialStatuses.includes('unavailable'));
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [errors, setErrors] = useState<{ projectId?: boolean; role?: boolean; startDate?: boolean }>({});

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });
  const { data: roleReqs = [] } = useQuery({ queryKey: ['training', 'role-requirements'], queryFn: () => api.get<RoleReq[]>('/training/role-requirements').then((r) => r.data) });

  const liveProjects = projects.filter((p) => p.status === 'live');
  const requester = contacts.find((c) => c.id === requesterId);

  // Auto-populate required skills from the role's mandatory cert list when the
  // user picks a role. Skip on edit (use the persisted requiredSkills) and skip
  // once the user has manually toggled anything in this session.
  useEffect(() => {
    if (editing || userTouchedSkills.current || !role) return;
    const req = roleReqs.find((r) => r.role.trim().toLowerCase() === role.trim().toLowerCase());
    setSkills(req?.mandatory ?? []);
  }, [role, roleReqs, editing]);

  const toggleSkill = (s: string) => {
    userTouchedSkills.current = true;
    setSkills((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  function validateAndSubmit() {
    const next: { projectId?: boolean; role?: boolean; startDate?: boolean } = {};
    if (!projectId) next.projectId = true;
    if (!role) next.role = true;
    if (!startDate) next.startDate = true;
    setErrors(next);
    if (Object.keys(next).length) {
      const missing: string[] = [];
      if (next.projectId) missing.push('Project');
      if (next.role) missing.push('Role');
      if (next.startDate) missing.push('Start Date');
      toast('Missing required fields', missing.join(', '), 'warn');
      return;
    }
    save.mutate();
  }

  const save = useMutation({
    mutationFn: async () => {
      const statuses: string[] = [];
      if (stAvail) statuses.push('available');
      if (stDep) statuses.push('deployed');
      if (stUnavail) statuses.push('unavailable');
      const payload = {
        projectId,
        role,
        startDate,
        duration,
        requiredSkills: skills,
        statusFilter: statuses,
        requesterId: requesterId || null,
        notes: notes.trim() || null,
      };
      if (editing) {
        return api.patch(`/vacancies/${editing.id}`, payload);
      }
      return api.post('/vacancies', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'active-vacancies'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      toast.success(editing ? 'Vacancy updated' : 'Vacancy search created', role);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{editing ? '✏ Edit Vacancy' : '⚑ New Vacancy Search'}</div>
        <div className="modal-sub">{editing ? 'Update vacancy details — role, dates, skills' : 'Find the best matched workers for a project role'}</div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Project</label>
            <SearchableSelect
              id="vac-project"
              value={projectId}
              onChange={(v) => { setProjectId(v); if (errors.projectId) setErrors((p) => ({ ...p, projectId: false })); }}
              placeholder="— Select project —"
              invalid={errors.projectId}
              options={liveProjects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Role Required</label>
            <SearchableSelect
              id="vac-role"
              value={role}
              onChange={(v) => { setRole(v); if (errors.role) setErrors((p) => ({ ...p, role: false })); }}
              placeholder="— Select role —"
              invalid={errors.role}
              options={roles.map((r) => ({ value: r, label: r }))}
            />
          </div>
        </div>
        <div className="form-row" style={{ marginBottom: 10 }}>
          <label className="form-label">Requested By <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>— contact making the request</span></label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <SearchableSelect
                id="vac-requester"
                value={requesterId}
                onChange={setRequesterId}
                placeholder="— Select contact (optional) —"
                options={[{ value: '', label: '— Select contact (optional) —' }, ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` · ${c.customer}` : ''}` }))]}
              />
            </div>
            <div id="vac-requester-info" style={{ fontSize: 10, color: 'var(--muted)', minWidth: 160 }}>
              {requester ? `${requester.role ?? ''}${requester.customer ? ` · ${requester.customer}` : ''}` : ''}
            </div>
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Start Date Required</label><input type="date" className="form-input" id="vac-start" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (errors.startDate) setErrors((p) => ({ ...p, startDate: false })); }} style={errors.startDate ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(239,68,68,.15)' } : undefined} /></div>
          <div className="form-row">
            <label className="form-label">Duration</label>
            <SearchableSelect
              id="vac-dur"
              value={duration}
              onChange={setDuration}
              placeholder="— Select duration —"
              options={durations.map((d) => ({ value: d, label: d }))}
            />
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Required Skills / Training (tick all that must be held)</label>
          <div id="vac-skills-checks" style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, maxHeight: 160, overflowY: 'auto' }}>
            {certTypes.map((c) => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', padding: '3px 7px', background: skills.includes(c) ? 'rgba(232,164,39,.15)' : 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5 }}>
                <input type="checkbox" checked={skills.includes(c)} onChange={() => toggleSkill(c)} style={{ accentColor: 'var(--accent)' }} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Worker Status Filter</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 5 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="vac-st-avail" checked={stAvail} onChange={(e) => setStAvail(e.target.checked)} style={{ accentColor: 'var(--live)' }} /> <span style={{ color: 'var(--live)' }}>● Available</span></label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="vac-st-dep" checked={stDep} onChange={(e) => setStDep(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> <span style={{ color: 'var(--accent)' }}>● Deployed</span></label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="vac-st-unavail" checked={stUnavail} onChange={(e) => setStUnavail(e.target.checked)} style={{ accentColor: 'var(--danger)' }} /> <span style={{ color: 'var(--danger)' }}>● Unavailable</span></label>
          </div>
        </div>
        <div className="form-row"><label className="form-label">Notes / Special Requirements</label><textarea className="form-textarea" id="vac-notes" placeholder="Any specific skills, certifications, or requirements…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={validateAndSubmit} disabled={save.isPending}>
            {save.isPending ? (editing ? 'Saving…' : 'Searching…') : editing ? '💾 Save Changes' : '🔍 Find Best Match'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
